import type { Album } from "../../../../types"

// SOL-192. The album drag payload crosses a component boundary as a JSON
// string in a DataTransfer, so nothing type-checks the producer against the
// consumer. AlbumCard wrote a bare album while Breadcrumbs required a tagged
// wrapper, and neither side could see the other was wrong -- the drop kept
// working only because Breadcrumbs fell through to its component-state
// branch. This module is the one place the shape is defined, so the two
// cannot drift apart again: both sides go through serialize/parse and never
// touch JSON.stringify/parse directly.

/** The only DataTransfer type this app writes. `text/plain` used to be read
 * as a fallback and was never written by anything -- a second dead path
 * alongside the one this ticket fixes -- so it is gone rather than kept
 * unexercised. */
export const ALBUM_DRAG_MIME = 'application/json'

/** Discriminator. `application/json` is not ours alone: any page can put JSON
 * on a drag, so a payload without this tag must not be read as an album. */
const ALBUM_DRAG_TYPE = 'album'

interface AlbumDragPayload {
  type: typeof ALBUM_DRAG_TYPE
  album: Album
}

export function serializeAlbumDrag(album: Album): string {
  const payload: AlbumDragPayload = { type: ALBUM_DRAG_TYPE, album }
  return JSON.stringify(payload)
}

/** Returns the album, or null for anything that isn't one of our payloads --
 * absent, empty, unparseable, or foreign JSON. Callers treat null as "no
 * album came over the wire" and fall back to their own drag state; it is
 * never an error worth surfacing, since a user can drag anything at all onto
 * the page. */
export function parseAlbumDrag(raw: string | null | undefined): Album | null {
  if (!raw || raw.trim() === '') return null

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }

  if (
    typeof parsed !== 'object' ||
    parsed === null ||
    (parsed as AlbumDragPayload).type !== ALBUM_DRAG_TYPE
  ) {
    return null
  }

  const { album } = parsed as AlbumDragPayload
  return album && typeof album.uuid === 'string' ? album : null
}
